import React, { forwardRef, useRef, useMemo } from 'react';
import * as RadixTabsPrimitive from '@radix-ui/react-tabs';
import { Icon, GDSIconSource } from '#components/icon/icon.tsx';
import { styled } from '../../theme';
import { GDSCustomizableComponent, GDSResponsiveProp } from '../../types';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import type { GDSTypographyToken } from '../../theme/utils/typography';
import { useTabElementIsInView } from './use-tab-element-is-in-view';
import { useTablistContext } from './tablist-context';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSTabProps
  extends RadixTabsPrimitive.TabsTriggerProps,
    GDSCustomizableComponent {
  icon?: GDSResponsiveProp<GDSIconSource>;
  iconLabel?: string;
  width?: GDSResponsiveProp<number | string>;
}
interface TabStyles {
  [key: string]: string | GDSTypographyToken | {};
}

const DISPLAY_NAME = 'Tab';

export const tabStyles: TabStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '$onSurfaceTextSubdued',
  padding: '$threeQuarters $half',
  border: 'none',
  borderBottomStyle: 'solid',
  borderBottomColor: 'transparent',
  borderBottomWidth: '$borderWidths$thick',
  backgroundColor: '$surfaceBackgroundPrimary',
  cursor: 'pointer',
  '&[aria-current="page"], &[data-state="active"], &.active': {
    color: '$interactiveActionPrimary',
    borderBottomColor: '$interactiveActionPrimary',
  },
  '&:hover': {
    color: '$interactiveActionHovered',
  },
  '&[class*="GDS"]:focus': {
    outlineOffset: '-$space$half',
  },
  typography: '$buttonTwo',
};

const BaseTab = styled(RadixTabsPrimitive.Trigger, {
  ...tabStyles,
});

export const TabIconLabelWrapper = styled('span', {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '$half',
});

export const Tab = forwardRef<HTMLButtonElement, GDSTabProps>(
  (
    {
      children,
      className,
      css,
      icon,
      iconLabel,
      width,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLButtonElement>(null);
    const composedRefs = useComposedRefs(ref, internalRef);
    const tabWidth = useResponsiveProp(width);
    const tabIcon = useResponsiveProp(icon);
    const { elementIsInView, tabScrollOptions } = useTabElementIsInView();
    const { scrollBackButton, scrollForwardButton } = useTablistContext();

    const scrollElementIfNotInView = useMemo(
      () => () => {
        if (
          !elementIsInView(internalRef) &&
          (scrollBackButton || scrollForwardButton)
        ) {
          internalRef.current!.scrollIntoView(tabScrollOptions);
        }
      },
      [
        elementIsInView,
        tabScrollOptions,
        scrollBackButton,
        scrollForwardButton,
      ],
    );

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <BaseTab
          className={[`GDS-tab`, className].join(' ')}
          css={{
            ...css,
            ...(width && {
              width: `${tabWidth}`,
            }),
          }}
          ref={composedRefs}
          onFocus={() => scrollElementIfNotInView()}
          data-testid={testId}
          {...props}
        >
          <TabIconLabelWrapper>
            {tabIcon && <Icon icon={tabIcon} label={iconLabel} />}
            {children}
          </TabIconLabelWrapper>
        </BaseTab>
      </GenesisCoreInspector>
    );
  },
);
